import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';

const ProfileScreen = ({ onBack, onNavigate, t, isRTL }) => {
  const { lang, setLang } = useLanguage();

  const tr = t || {};
  const fb = (k, v) => tr[k] || v; // fallback helper

  const menuItems = [
    {
      section: fb('sectionAccount', 'Account'),
      items: [
        { id: 'personal',  icon: '👤', label: fb('personalInfo', 'Personal Information'), desc: fb('personalInfoDesc', 'Name, email, phone'), action: () => {} },
        { id: 'simoid',    icon: '🆔', label: fb('simoIdLabel', 'SimoPay ID'),            desc: 'ahmed@simopay',                              action: () => {} },
        { id: 'kyc',       icon: '✓',  label: fb('kycVerification', 'KYC Verification'),  desc: fb('kycVerified', 'Verified'),                  status: 'verified', action: () => {} },
      ],
    },
    {
      section: fb('sectionSecurity', 'Security'),
      items: [
        { id: 'password',  icon: '🔒', label: fb('changePassword', 'Change Password'), action: () => {} },
        { id: 'biometric', icon: '👆', label: fb('biometricLock', 'Biometric Lock'), toggle: true, value: true, action: () => {} },
        { id: 'pin',       icon: '🔢', label: fb('transactionPIN', 'Transaction PIN'), action: () => {} },
      ],
    },
    {
      section: fb('sectionPreferences', 'Preferences'),
      items: [
        {
          id: 'language',
          icon: '🌐',
          label: fb('languageLabel', 'Language'),
          desc: lang === 'ar' ? fb('languageArabic', 'العربية') : fb('languageEnglish', 'English'),
          // Tapping the language row toggles between English ↔ Arabic
          action: () => setLang(lang === 'en' ? 'ar' : 'en'),
        },
        { id: 'currency',      icon: '💰', label: fb('defaultCurrency', 'Default Currency'), desc: 'MAD', action: () => {} },
        { id: 'notifications', icon: '🔔', label: fb('notifications', 'Notifications'), toggle: true, value: true, action: () => {} },
      ],
    },
    {
      section: fb('sectionSupport', 'Support'),
      items: [
        { id: 'help',    icon: '❓', label: fb('helpCenter', 'Help Center'),     action: () => {} },
        { id: 'contact', icon: '💬', label: fb('contactSupport', 'Contact Support'), action: () => {} },
        { id: 'terms',   icon: '📄', label: fb('termsPrivacy', 'Terms & Privacy'), action: () => {} },
      ],
    },
  ];

  return (
    <div
      data-testid="profile-screen"
      className="no-scrollbar"
      style={{
        background: '#0F0F0F',
        height: '100%',
        fontFamily: 'Inter, sans-serif',
        overflowY: 'auto',
        paddingBottom: '12px',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Header */}
      <div style={{ padding: '18px 20px 20px' }}>
        <h2 style={{ color: '#F5F1EA', fontSize: '20px', fontWeight: '800', margin: 0, letterSpacing: '-0.7px' }}>
          {fb('profileTitle', 'Profile')}
        </h2>
      </div>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ padding: '0 20px 24px' }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(255,255,255,0.02) 100%)',
            border: '1px solid rgba(212,175,55,0.2)',
            borderRadius: '20px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4AF37, #B8962E)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '800', color: '#0F0F0F', flexShrink: 0 }}>A</div>
          <div style={{ flex: 1 }}>
            <p style={{ color: '#F5F1EA', fontSize: '18px', fontWeight: '700', margin: '0 0 2px', letterSpacing: '-0.4px' }}>Ahmed</p>
            <p style={{ color: '#D4AF37', fontSize: '13px', margin: '0 0 4px', fontWeight: '500' }}>ahmed@simopay</p>
            <div style={{ display: 'inline-flex', background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: '100px', padding: '3px 10px' }}>
              <span style={{ color: '#4ADE80', fontSize: '10px', fontWeight: '600' }}>{fb('verified', '✓ Verified')}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Menu Sections */}
      <div style={{ padding: '0 20px' }}>
        {menuItems.map((section, sIdx) => (
          <div key={sIdx} style={{ marginBottom: '24px' }}>
            <p style={{ color: '#8B93A7', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontWeight: '600' }}>
              {section.section}
            </p>
            {section.items.map((item, iIdx) => (
              <motion.div
                key={item.id}
                whileTap={{ scale: 0.98 }}
                onClick={item.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '14px',
                  marginBottom: iIdx < section.items.length - 1 ? '8px' : '0',
                  cursor: 'pointer',
                }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '13px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: '#F5F1EA', fontSize: '14px', fontWeight: '600', margin: 0 }}>{item.label}</p>
                  {item.desc && (
                    <p style={{ color: '#8B93A7', fontSize: '12px', margin: '2px 0 0' }}>{item.desc}</p>
                  )}
                  {item.status === 'verified' && (
                    <div style={{ display: 'inline-flex', background: 'rgba(74,222,128,0.1)', borderRadius: '4px', padding: '2px 6px', marginTop: '4px' }}>
                      <span style={{ color: '#4ADE80', fontSize: '10px', fontWeight: '600' }}>{fb('verified', '✓ Verified')}</span>
                    </div>
                  )}
                </div>
                {item.toggle ? (
                  <div style={{ width: '44px', height: '24px', borderRadius: '12px', background: item.value ? '#D4AF37' : 'rgba(255,255,255,0.1)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#0F0F0F', position: 'absolute', top: '2px', left: item.value ? '22px' : '2px', transition: 'left 0.2s' }} />
                  </div>
                ) : (
                  <span style={{ color: '#8B93A7', fontSize: '16px', flexShrink: 0 }}>{isRTL ? '←' : '→'}</span>
                )}
              </motion.div>
            ))}
          </div>
        ))}

        {/* Logout Button */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          style={{
            width: '100%',
            padding: '16px',
            background: 'rgba(255,100,100,0.08)',
            border: '1px solid rgba(255,100,100,0.2)',
            borderRadius: '14px',
            color: '#FF6464',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '12px',
          }}
        >
          {fb('logout', 'Log Out')}
        </motion.button>

        <p style={{ color: '#5C6478', fontSize: '11px', textAlign: 'center', margin: '0 0 4px' }}>
          {fb('appVersion', 'SIMO Pay v1.0.0')}
        </p>
        <p style={{ color: '#5C6478', fontSize: '11px', textAlign: 'center', margin: '0 0 80px' }}>
          {fb('moroccoMade', 'Made in Morocco 🇲🇦')}
        </p>
      </div>
    </div>
  );
};

export default ProfileScreen;
